'use client';

import { type ReactNode } from 'react';
import { Button } from '@/components/Button/Button';
import { NavbarMenuModal } from '../Modal/NavbarMenuModal/NavbarMenuModal';
import { analyticsEventHandlers } from '@/lib/analytics/events/generalAnalyticsEvents';

import './Navbar.scss';

type NavbarLinks = Array<{ label: string; url: string }>;
type NavbarProps = { children?: ReactNode; mobileChildren?: ReactNode; className?: string; logoRedirectUrl: string; infoLinks?: NavbarLinks };
type NavbarLinksProps = { infoLinks?: NavbarLinks };

function NavbarLinks({ infoLinks }: NavbarLinksProps) {
	return infoLinks ? (
		<>
			{infoLinks.slice(0, 3).map((link, index) => (
				<Button
					key={link.label}
					href={link.url}
					variant="navbar"
					as="a"
					data-testid={`btn-navbar-${index}`}
					onClick={() => analyticsEventHandlers.trackNavigationLinkClicked(link.url)}
				>
					{link.label}
				</Button>
			))}
		</>
	) : null;
}

export function Navbar({ children, mobileChildren, className = '', logoRedirectUrl, infoLinks }: NavbarProps) {
	return (
		<nav className={`[ navbar ] [ d-flex px-sm color-fill-white ${className} ]`}>
			{/* Logo */}
			<a href={logoRedirectUrl} className="d-flex color-text-black" title="Toyota BZ4X" data-testid="toyota-logo">
				<svg width="81" height="56" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path
						d="M42.329 42.159c2.44-1.83 4.21-7.221 4.21-13.588 0-.252-.004-.501-.009-.75 7.08-1.072 12.154-3.979 12.154-7.397 0-.156-.01-.31-.031-.463 1.802 2.186 2.834 4.727 2.834 7.439 0 7.747-8.424 14.105-19.158 14.759Zm-22.816-14.76c0-2.71 1.032-5.252 2.834-7.439-.02.154-.031.308-.031.464 0 3.418 5.075 6.325 12.154 7.398a37.09 37.09 0 0 0-.008.749c0 6.367 1.768 11.759 4.21 13.588-10.735-.654-19.16-7.012-19.16-14.76ZM40.5 14.318c-2.63 0-4.868 3.97-5.696 9.512-4.982-.786-8.46-2.57-8.46-4.646 0-2.802 6.338-5.074 14.156-5.074 7.818 0 14.156 2.272 14.156 5.074 0 2.076-3.478 3.86-8.46 4.646-.828-5.541-3.065-9.512-5.696-9.512ZM36.99 24.1c.549-3.56 1.913-6.081 3.51-6.081 1.597 0 2.961 2.522 3.51 6.081a38.634 38.634 0 0 1-3.51.157c-1.211 0-2.387-.054-3.51-.157Zm3.51 13.033c-2.011 0-3.654-4.001-3.76-9.037 1.213.11 2.47.168 3.76.168 1.29 0 2.547-.058 3.76-.168-.106 5.036-1.749 9.037-3.76 9.037ZM40.5 12C26.97 12 16 19.163 16 28s10.97 16 24.5 16S65 36.837 65 28 54.03 12 40.5 12Z"
						fill="currentColor"
					></path>
				</svg>
			</a>
			<div className="repel w-full">
				{/* Left Aligned Children's */}
				<div className="d-none nav:d-flex items-center gap-2xs h-full">
					<NavbarLinks infoLinks={infoLinks} />
				</div>

				{/* Right Aligned Children's */}
				<div className="d-flex items-center gap-2xs h-full ml-auto">
					<div className="d-none md:d-flex items-center gap-2xs h-full">{children}</div>

					{/* Mobile Menu */}
					{mobileChildren && (
						<NavbarMenuModal modalTriggerAsChild>
							<div className="d-flex d-flex-column gap-sm md:d-none mb-md">{mobileChildren}</div>

							<div className="d-flex d-flex-column items-start gap-sm">
								<NavbarLinks infoLinks={infoLinks} />
							</div>
						</NavbarMenuModal>
					)}
				</div>
			</div>
		</nav>
	);
}
